import type { EChartsOption } from 'echarts';
import type { ChartData } from '../../types/dashboard';
import { formatValue, formatActual } from '../../utils/numberFormat';

/**
 * Repo-2 branding pass:
 * CHG finance chart palette, scoped to Smart Insights chart rendering only.
 */
export const FALLBACK_PALETTE = [
  '#2C5F7C',
  '#E8B84F',
  '#5E8B5E',
  '#D97757',
  '#6FB0D1',
  '#A63D40',
  '#8CB58C',
  '#8A8178',
];

// All chart number formatting delegates to the canonical policy in
// utils/numberFormat (single source of truth). These thin wrappers keep
// the existing chart call-sites stable.
export const formatNumberShort = (value: number): string => {
  if (value == null || isNaN(value)) return '';
  return formatValue(value);
};

export const formatNumberFull = (value: number, format?: string): string => {
  if (value == null || isNaN(value)) return '';
  return formatActual(value, { format });
};

// Tooltips are the hover surface: show the FULL actual value (never
// abbreviated) so users always see the real number behind a rounded label.
export const formatTooltipValue = (val: unknown): string => {
  if (val == null) return '';
  if (typeof val !== 'number' && isNaN(Number(val))) return String(val);
  return formatActual(val);
};

export const stripChartPrefix = (str: string): string =>
  str.replace(/^Chart-\d+\s+/i, '');

export const formatLabelFromKey = (key?: string): string =>
  key ? stripChartPrefix(key).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '';

const CATEGORY_CANDIDATE_KEYS = ['name', 'label', 'category', 'date', 'x', 'dimension'];

export function isNumericLike(value: unknown): boolean {
  if (value == null || value === '') return false;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return false;
    return Number.isFinite(Number(trimmed.replace(/,/g, '')));
  }
  return false;
}

export function toNumericValue(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Number(value.trim().replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

export function getCategoryValue(row: Record<string, unknown>, xFieldKey?: string): string {
  // Caller-supplied x-axis column wins. Required when the x-axis dim
  // is numerically encoded (e.g. Fiscal_Qrtr_Fisca = 20264): the
  // generic heuristic below skips numeric keys and returns '' so the
  // chart renders without tick labels.
  if (xFieldKey) {
    const value = row[xFieldKey];
    if (value != null && value !== '') return String(value);
  }

  for (const key of CATEGORY_CANDIDATE_KEYS) {
    const value = row[key];
    if (value != null && value !== '') return String(value);
  }

  const fallbackKey = Object.keys(row).find((key) => !isNumericLike(row[key]));
  if (fallbackKey) return String(row[fallbackKey] ?? '');

  return String(row.name ?? row.label ?? '');
}

export function getCategoryAxisLabelLayout(
  data: Record<string, unknown>[],
  xFieldKey?: string,
): {
  maxLabelLength: number;
  shouldUseVerticalLabels: boolean;
  axisLabelWidth: number;
  gridBottom: number;
  nameGap: number;
  truncateAt: number;
} {
  const categories = data.map((d) => getCategoryValue(d, xFieldKey));
  const maxLabelLength = categories.reduce((max, item) => Math.max(max, item.length), 0);
  const shouldUseVerticalLabels = data.length > 8 || (data.length > 4 && maxLabelLength > 18) || maxLabelLength > 32;
  const verticalTruncateAt = Math.min(24, Math.max(14, maxLabelLength));
  const horizontalTruncateAt = data.length <= 4 ? 28 : 22;
  // Rotated labels project their text length into the bottom gutter.
  // Keep them readable, but cap the displayed text so tick labels and
  // the axis title do not collide on narrow chart cards.
  const verticalLabelDepth = Math.min(148, 52 + verticalTruncateAt * 3.8);

  return {
    maxLabelLength,
    shouldUseVerticalLabels,
    axisLabelWidth: shouldUseVerticalLabels ? verticalLabelDepth : data.length <= 4 ? 180 : 140,
    gridBottom: shouldUseVerticalLabels ? verticalLabelDepth + 34 : 58,
    nameGap: shouldUseVerticalLabels ? verticalLabelDepth + 22 : 42,
    truncateAt: shouldUseVerticalLabels ? verticalTruncateAt : horizontalTruncateAt,
  };
}

export interface ChartComponentProps {
  chart: ChartData;
  height?: number;
  colors?: string[];
  presentation?: {
    hideXAxisLabels?: boolean;
    hideYAxisLabels?: boolean;
    hideRightYAxisLabels?: boolean;
    hideXAxisName?: boolean;
    hideYAxisName?: boolean;
    enableRangeSlider?: boolean;
    showValueLabels?: boolean;
    /** Let a parent surface own overflow instead of creating a nested chart scroller. */
    useExternalScroll?: boolean;
    gridOverrides?: Partial<NonNullable<EChartsOption['grid']>>;
  };
}

export function getDisplayName(chart: ChartData, field: string): string {
  return chart.metric_display_names?.[field]
    ?? stripChartPrefix(field).replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
}

export function getXAxisFieldKey(chart: ChartData): string | undefined {
  return chart.lineage_v2?.dimensions?.find((d) => d.slot === 'x_axis')?.column;
}

export function shouldShowBarValueLabels(
  chart: ChartData,
  data: Record<string, unknown>[],
  fields: string[] = getNumericFields(data, [getXAxisFieldKey(chart)])
): boolean {
  if (data.length === 0) return false;
  const isHorizontal = chart.type === 'horizontal_bar' || chart.type === 'diverging_bar';
  const isStacked = chart.type === 'stacked_bar';
  if (isStacked) return false;

  if (isHorizontal) {
    return data.length <= 16 && fields.length <= 2;
  }

  const xFieldKey = getXAxisFieldKey(chart);
  const labelLayout = getCategoryAxisLabelLayout(data, xFieldKey);
  if (labelLayout.shouldUseVerticalLabels) return false;

  if (fields.length > 1) {
    return data.length <= 3;
  }

  return data.length <= 6;
}

export function getCartesianChartGrid(
  chart: ChartData,
  data: Record<string, unknown>[],
  fields: string[] = getNumericFields(data, [getXAxisFieldKey(chart)])
): { top: number; right: number; bottom: number; left: number; plotMinHeight: number } {
  const hasLegend = fields.length > 1;
  const isBarLike = ['bar', 'multi_column_bar', 'stacked_bar', 'histogram'].includes(chart.type);
  const showValueLabels = isBarLike && shouldShowBarValueLabels(chart, data, fields);
  const xFieldKey = getXAxisFieldKey(chart);
  const labelLayout = getCategoryAxisLabelLayout(data, xFieldKey);

  return {
    // Top padding tightened a notch — value labels still get room
    // but the legend doesn't need a fat margin under it.
    top: hasLegend ? (showValueLabels ? 70 : 48) : (showValueLabels ? 52 : 30),
    // Slightly slimmer right gutter — leaves more pixels for the
    // plot itself without crowding the rightmost tick.
    right: 18,
    bottom: labelLayout.gridBottom,
    // Tighter left gutter (was 44) — y-axis tick labels are
    // formatted compactly (formatNumberShort) so they rarely need
    // 44px of breathing room.
    left: 38,
    // Bigger plot floor when labels go vertical so the chart canvas
    // doesn't shrink down to a postage stamp once we steal 100+
    // pixels for the rotated names.
    plotMinHeight: labelLayout.shouldUseVerticalLabels ? 240 : 200,
  };
}

export function getNumericFields(
  data: Record<string, unknown>[],
  excludedKeys: Array<string | null | undefined> = [],
): string[] {
  if (!data || data.length === 0) return ['value'];
  const excluded = new Set(excludedKeys.filter((key): key is string => typeof key === 'string' && key.length > 0));
  const keys = Array.from(new Set(data.flatMap((row) => Object.keys(row))));
  const fields = keys.filter((key) => {
    if (CATEGORY_CANDIDATE_KEYS.includes(key) || excluded.has(key)) return false;
    const definedValues = data
      .map((row) => row[key])
      .filter((value) => value !== null && value !== undefined && value !== '');
    return definedValues.length > 0 && definedValues.every((value) => isNumericLike(value));
  });
  if (fields.length >= 1) return fields;
  return ['value'];
}

export function getChartNumericFields(chart: ChartData, data: Record<string, unknown>[]): string[] {
  const seriesFields = Array.isArray(chart.series)
    ? chart.series.map((series) => series.field).filter((field): field is string => typeof field === 'string' && field.length > 0)
    : [];
  if (seriesFields.length > 0) {
    return Array.from(new Set(seriesFields));
  }

  const yFields = Array.isArray(chart.y_field)
    ? chart.y_field
    : typeof chart.y_field === 'string' && chart.y_field.length > 0
      ? [chart.y_field]
      : [];
  const explicitFields = [...yFields, ...(chart.secondary_y || [])].filter((field) =>
    data.some((row) => row[field] !== null && row[field] !== undefined && row[field] !== '')
  );
  if (explicitFields.length > 0) {
    return Array.from(new Set(explicitFields));
  }

  return getNumericFields(data, [getXAxisFieldKey(chart)]);
}

/** Value stats for tooltip: max, min, and min value > 0 (smallest positive). */
export function getChartValueStats(
  data: Record<string, unknown>[],
  fields?: string[]
): { min: number; max: number; minPositive: number | null } {
  const f = fields ?? getNumericFields(data);
  let min = Infinity;
  let max = -Infinity;
  let minPositive: number | null = null;
  for (const row of data) {
    for (const key of f) {
      const v = toNumericValue(row[key] ?? row.value ?? 0);
      if (!Number.isNaN(v)) {
        if (v < min) min = v;
        if (v > max) max = v;
        if (v > 0 && (minPositive == null || v < minPositive)) minPositive = v;
      }
    }
  }
  return {
    min: min === Infinity ? 0 : min,
    max: max === -Infinity ? 0 : max,
    minPositive,
  };
}

export function getChartValueRange(
  data: Record<string, unknown>[],
  fields?: string[]
): { min: number; max: number } {
  const stats = getChartValueStats(data, fields);
  return { min: stats.min, max: stats.max };
}

function niceStep(roughStep: number): number {
  if (!isFinite(roughStep) || roughStep <= 0) return 1;
  const power = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalized = roughStep / power;
  if (normalized <= 1) return power;
  if (normalized <= 2) return 2 * power;
  if (normalized <= 5) return 5 * power;
  return 10 * power;
}

export function getNiceTickValues(min: number, max: number, desiredTicks = 5): number[] {
  if (!isFinite(min) || !isFinite(max)) return [0];
  if (min === max) {
    if (min === 0) return [0, 1];
    return [Math.min(0, min), min, Math.max(0, max)];
  }

  const safeDesired = Math.max(2, desiredTicks);
  const roughStep = Math.abs(max - min) / (safeDesired - 1);
  const step = niceStep(roughStep);
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;
  const ticks: number[] = [];

  for (let value = niceMin; value <= niceMax + (step * 0.5); value += step) {
    ticks.push(Number(value.toFixed(10)));
  }

  return ticks.length > 0 ? ticks : [0];
}

export const HORIZONTAL_VALUE_LABEL_RIGHT_RESERVE = 128;
export const HORIZONTAL_VALUE_LABEL_AXIS_PADDING_RATIO = 0.26;

export function getHorizontalValueAxisScale(
  data: Record<string, unknown>[],
  fields: string[],
  showValueLabels: boolean,
  desiredTicks = 5,
): { min: number; max: number; ticks: number[] } {
  const valueRange = getChartValueStats(data, fields);
  const tickMinInput = Math.min(0, valueRange.min);
  const tickMaxInput = Math.max(0, valueRange.max);
  const ticks = getNiceTickValues(tickMinInput, tickMaxInput, desiredTicks);
  const firstTick = ticks[0] ?? 0;
  const lastTick = ticks[ticks.length - 1] ?? 1;

  if (!showValueLabels) {
    return { min: firstTick, max: lastTick, ticks };
  }

  const rawSpan = Math.max(1, Math.abs(valueRange.max - valueRange.min));
  const positivePadding = Math.max(rawSpan * HORIZONTAL_VALUE_LABEL_AXIS_PADDING_RATIO, Math.abs(valueRange.max) * 0.08, 1);
  const negativePadding = Math.max(rawSpan * HORIZONTAL_VALUE_LABEL_AXIS_PADDING_RATIO, Math.abs(valueRange.min) * 0.08, 1);

  return {
    min: valueRange.min < 0 ? firstTick - negativePadding : firstTick,
    max: lastTick + positivePadding,
    ticks,
  };
}

/** Format a single line for tooltip: Max / Min / Zero ref / Min > 0. */
export function formatTooltipStatsLine(stats: { min: number; max: number; minPositive: number | null }): string {
  const parts: string[] = [
    `Max: ${formatNumberShort(stats.max)}`,
    `Min: ${formatNumberShort(stats.min)}`,
    'Zero: 0',
  ];
  if (stats.minPositive != null) parts.push(`Min &gt; 0: ${formatNumberShort(stats.minPositive)}`);
  return parts.join(' · ');
}

export const ECHARTS_OPTS = {
  renderer: 'canvas' as const,
  devicePixelRatio: typeof window !== 'undefined' ? (window.devicePixelRatio || 2) : 2,
  useDirtyRect: false,
};

/** Min left grid so Y-axis label and values stay inside card (no clipping) */
export const CHART_GRID_LEFT = 30;

/** Label font size for chart text */
export const CHART_LABEL_FONT_SIZE = 12;

/** Repo-2 chart text and surface tokens. */
export const CHART_TEXT_COLOR = '#1F1A20';
export const CHART_MUTED_TEXT_COLOR = '#7B6B72';
export const CHART_MUTED_SOFT_TEXT_COLOR = '#A8989F';
export const CHART_AXIS_LINE_COLOR = '#D9CFC4';
export const CHART_GRID_LINE_COLOR = '#EDE5D8';
export const CHART_BORDER_COLOR = '#EDE6E1';
export const CHART_TOOLTIP_BG = 'rgba(30,42,50,0.96)';
export const CHART_TOOLTIP_TEXT = '#F7F3EE';
export const CHART_POSITIVE_COLOR = '#3F7D4F';
export const CHART_NEGATIVE_COLOR = '#B23A48';
export const CHART_PLAN_COLOR = '#C9A24E';
export const CHART_TEAL_COLOR = '#4E7C89';

export function getBaseOption(colors?: string[]): EChartsOption {
  return {
    color: colors && colors.length > 0 ? colors : FALLBACK_PALETTE,
    grid: {
      top: 68,
      right: 24,
      bottom: 58,
      left: 44,
      containLabel: true,
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: CHART_TOOLTIP_BG,
      borderColor: CHART_TOOLTIP_BG,
      borderWidth: 1,
      textStyle: { color: CHART_TOOLTIP_TEXT, fontSize: CHART_LABEL_FONT_SIZE },
      extraCssText: 'box-shadow: 0 6px 18px rgba(31,26,32,0.18); border-radius: 8px;',
      confine: true,
    },
    legend: {
      top: 8,
      left: 0,
      right: 8,
      itemGap: 14,
      textStyle: { fontSize: CHART_LABEL_FONT_SIZE, color: CHART_TEXT_COLOR },
      itemWidth: 14,
      itemHeight: 10,
      formatter: (value: string) => value.length > 24 ? `${value.slice(0, 24)}...` : value,
    },
    textStyle: { color: CHART_TEXT_COLOR },
  };
}

export function buildCategoryAxis(chart: ChartData, data: Record<string, unknown>[]): EChartsOption['xAxis'] {
  // Axis label sources, in order: explicit ``x_axis_label`` from the
  // chart spec; first x-dim label from the canonical lineage payload;
  // otherwise empty. Legacy ``drillDown.chartMetadata`` axis-label
  // fallback has been retired alongside the field itself.
  const lineageDim = chart.lineage_v2?.dimensions?.find((d) => d.slot === 'x_axis');
  const label = chart.x_axis_label
    ?? lineageDim?.label
    ?? (lineageDim?.column ? formatLabelFromKey(lineageDim.column) : '');
  const xFieldKey = getXAxisFieldKey(chart);
  const categories = data.map((d) => getCategoryValue(d, xFieldKey));
  const labelLayout = getCategoryAxisLabelLayout(data, xFieldKey);
  return {
    type: 'category',
    data: categories,
    axisLabel: {
      fontSize: CHART_LABEL_FONT_SIZE,
      color: CHART_MUTED_TEXT_COLOR,
      rotate: labelLayout.shouldUseVerticalLabels ? 90 : 0,
      hideOverlap: true,
      interval: 0,
      margin: labelLayout.shouldUseVerticalLabels ? 12 : 8,
      width: labelLayout.axisLabelWidth,
      overflow: 'truncate',
      formatter: (value: string) => value && value.length > labelLayout.truncateAt ? `${value.slice(0, labelLayout.truncateAt - 3)}...` : value,
    },
    axisLine: { lineStyle: { color: CHART_AXIS_LINE_COLOR } },
    axisTick: { lineStyle: { color: CHART_AXIS_LINE_COLOR } },
    name: label,
    nameLocation: 'middle',
    nameGap: labelLayout.nameGap,
    nameTextStyle: { fontSize: CHART_LABEL_FONT_SIZE, color: CHART_MUTED_TEXT_COLOR },
  };
}

export function buildValueAxis(chart: ChartData, fields: string[]): EChartsOption['yAxis'] {
  // Same lineage-driven fallback chain as ``buildCategoryAxis`` —
  // explicit ``y_axis_label`` first, then humanised single-field
  // display name. Legacy ``drillDown.chartMetadata.yAxisLabel`` is
  // gone.
  const label = chart.y_axis_label
    ?? (fields.length === 1 ? getDisplayName(chart, fields[0]) : '');
  const nameGap = label.length > 24 ? 64 : 52;
  return {
    type: 'value',
    axisLabel: { fontSize: CHART_LABEL_FONT_SIZE, color: CHART_MUTED_TEXT_COLOR, formatter: (v: number) => formatNumberShort(v) },
    axisLine: { show: false },
    axisTick: { show: false },
    splitLine: { lineStyle: { color: CHART_GRID_LINE_COLOR } },
    name: label,
    nameLocation: 'middle',
    nameGap,
    nameTextStyle: { fontSize: CHART_LABEL_FONT_SIZE, color: CHART_MUTED_TEXT_COLOR },
  };
}
